import {
  type CreateOrderItemDTO,
  type DiscountDTO,
  type Option,
  type OrderItemDTO,
  type StorefrontOrderLine
} from 'ecosystem'
import { convertMinorUnitToValue, roundNumber } from 'shared-utils'
import { orderLineValueCalculator } from './payment'
import { klarnaOrderLineFactory } from './klarna-utils'

export const parseStoreToBackendOrderLines = (
  items: StorefrontOrderLine[],
  cartDiscountCode: Option<DiscountDTO>
): CreateOrderItemDTO[] =>
  items.map((orderLine) => {
    const { product, services, subItems, ...restOfProduct } = orderLine
    const { totalAmount, unitPrice } = orderLineValueCalculator(orderLine, cartDiscountCode)

    return {
      ...restOfProduct,
      // the original unit price refers to a price before a discount code is applied
      originalUnitPrice: convertMinorUnitToValue(unitPrice),
      /**
       * The line total spread across every unit — the blended figure an
       * `OrderItem`'s single `unitPrice` forces on us. A `PACK` quantity
       * discount can price one line in two runs (20 at −10%, 5 at full), and
       * only the payment providers can carry that split; the order snapshots
       * the average, which keeps the line total right.
       *
       * Rounded to 2 decimals rather than left as a raw division: an uneven
       * split otherwise leaves sub-öre tails that drift the order total away
       * from the amount the provider actually authorised.
       */
      unitPrice: roundNumber(convertMinorUnitToValue(totalAmount / restOfProduct.quantity), 2),
      productId: product.id,
      name: product.name,
      brand: product.brand?.name || '',
      slug: product.slug,
      services: services.map((s) => ({
        quantity: s.quantity,
        totalAmount: s.totalAmount,
        serviceId: s.service.id
      })),
      additionalInfo: '',
      subItems
    }
  })

export const computeFromStoreFrontOrderLines = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string
) => {
  const { orderAmount } = klarnaOrderLineFactory(items, cartDiscountCode, countryCode)

  return { orderAmount: convertMinorUnitToValue(orderAmount) }
}

/**
 * Compute the BE-shaped order totals from a gross-priced `CreateOrderItemDTO[]`.
 *
 * Per FEATURE_B2B_ORDER_PLACEMENT.md §3 / §8 `orderAmount` is the gross order
 * total with `shippingCost` **included** — the same semantics on UPFRONT and
 * INVOICE, so nothing downstream branches on `paymentType` to read an amount.
 * `shippingCost` travels alongside as a breakdown of the freight portion, never
 * as an addend: summing the two double-counts freight. So:
 *
 * - `orderAmount` — gross items total (sum of `unitPrice × quantity`,
 *   post-discount per `parseStoreToBackendOrderLines`) **plus**
 *   `shippingCostGross`.
 * - `orderTaxAmount` — the VAT already inside `orderAmount`, covering goods and
 *   freight: per line `lineGross × vatRate / (100 + vatRate)`, and the same for
 *   shipping (so callers must supply `shippingVatRate` when freight has VAT).
 * - `orderDiscount` — total discount the cart code applied, derived from
 *   `(originalUnitPrice − unitPrice) × quantity` per line. Zero otherwise.
 *
 * Shared by both order flows: invoice (SF requires FE-supplied totals) and
 * the upfront flow when/if it migrates to FE-supplied totals.
 */
export const computeOrderTotalsFromGross = (
  items: CreateOrderItemDTO[],
  shippingCostGross = 0,
  shippingVatRate = 0
): { orderAmount: number; orderTaxAmount: number; orderDiscount: number } => {
  let orderAmount = 0
  let orderTaxAmount = 0
  let orderDiscount = 0
  const walk = (lines: CreateOrderItemDTO[]) => {
    lines.forEach((item) => {
      const lineGross = item.unitPrice * item.quantity
      orderAmount += lineGross
      if (item.vatRate) {
        orderTaxAmount += (lineGross * item.vatRate) / (100 + item.vatRate)
      }
      const lineDiscount = (item.originalUnitPrice - item.unitPrice) * item.quantity
      if (lineDiscount > 0) orderDiscount += lineDiscount
      walk(item.subItems ?? [])
    })
  }
  walk(items)
  // Freight is part of the order total: the cost lands in `orderAmount`, its VAT
  // in `orderTaxAmount`. `shippingCost` is still reported on its own root field,
  // as a breakdown of this total rather than something to add to it.
  orderAmount += shippingCostGross
  if (shippingCostGross && shippingVatRate) {
    orderTaxAmount += (shippingCostGross * shippingVatRate) / (100 + shippingVatRate)
  }
  return {
    orderAmount: roundNumber(orderAmount),
    orderTaxAmount: roundNumber(orderTaxAmount),
    orderDiscount: roundNumber(orderDiscount)
  }
}

// parseCartSubItemsToBackendOrderSubItems now lives in ./cartSubItems (extracted so
// client components can import it without pulling in this file's fetching/credentials
// chain). It is re-exported from the package barrel via that module.

export const transformSlugParamsIntoSlug = (slugArr: string[]) => {
  if (slugArr.length === 1) {
    return slugArr[0]
  }
  return slugArr.join('/')
}

export const transformOrderItemsToCreateOrderItems = (
  orderItems: OrderItemDTO[]
): CreateOrderItemDTO[] => {
  return orderItems.map(({ product, ...i }) => ({
    ...i,
    productId: product.id,
    services:
      i.services?.map((service) => ({
        quantity: service.quantity,
        serviceId: service.service.id,
        totalAmount: service.totalAmount
      })) || [],
    subItems: transformOrderItemsToCreateOrderItems(i.subItems || [])
  }))
}
